import { useState, useEffect, useMemo, useRef } from 'react'

import { ArrowUp01Icon, ArrowDown01Icon } from '@hugeicons/react'

import { useJourneysStore } from '@/shared/store/useJourneysStore'
import { Blocker } from '@/shared/ui/Blocker'
import { Screen } from '@/shared/ui/Layout'

import { useTrackJourneyAnalytics } from './analytics'
import { JourneyShowHero } from './components/JourneyShowHero'
import { DigitalProductJourneyStageCard } from './Stages/Card'

interface DigitalProductJourneysShowProps {
  id: string
}

export const DigitalProductJourneysShow = ({
  id
}: DigitalProductJourneysShowProps) => {
  const { journey } = useJourneysStore({ uid: id })

  useTrackJourneyAnalytics({ journey })

  const [isVisible, setIsVisible] = useState(false)
  const [offscreenDirection, setOffscreenDirection] = useState(null)
  const [closedPhaseUids, setClosedPhaseUids] = useState<Set<string>>(new Set())
  const scrollBodyRef = useRef<HTMLDivElement | null>(null)

  const currentLevelUid = journey?._currentLevel?.uid

  const stages = useMemo(
    () =>
      journey?._stages.map((stage, index) => ({ stage, index })) ?? [],
    [journey]
  )

  const togglePhase = (uid: string) => {
    setClosedPhaseUids(prev => {
      const next = new Set(prev)
      if (next.has(uid)) next.delete(uid)
      else next.add(uid)
      return next
    })
  }

  useEffect(() => {
    const target = document.getElementById(`level-${currentLevelUid}`)
    const scrollRoot = scrollBodyRef?.current

    if (!target || !scrollRoot) {
      return () => {}
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        setIsVisible(entry.isIntersecting)

        if (!entry.isIntersecting) {
          const rootRect = scrollRoot.getBoundingClientRect()

          if (entry.boundingClientRect.top < rootRect.top) {
            setOffscreenDirection('up')
          } else if (entry.boundingClientRect.top > rootRect.bottom) {
            setOffscreenDirection('down')
          } else {
            setOffscreenDirection(null)
          }
        } else {
          setOffscreenDirection(null)
        }
      },
      { root: scrollRoot, threshold: 0.1 }
    )

    observer.observe(target)

    return () => {
      observer.disconnect()
    }
  }, [currentLevelUid, scrollBodyRef])

  useEffect(() => {
    if (!currentLevelUid) return () => {}

    const timer = setTimeout(() => {
      document
        .getElementById(`level-${currentLevelUid}`)
        ?.scrollIntoView({ behavior: 'smooth', block: 'center' })
    }, 300)

    return () => clearTimeout(timer)
  }, [currentLevelUid])

  if (!journey) return null

  return (
    <>
      <Screen>
        <Screen.Context fullBleed className="mb-3">
          <JourneyShowHero journey={journey} />
        </Screen.Context>

        <Screen.Content scrollBodyRef={scrollBodyRef} className="pt-1">
          <Blocker
            blockCondition={
              journey._blockCondition ?? { type: 'and', conditions: [] }
            }
          >
            <div className="mb-10 flex flex-col">
              {stages.map(({ stage, index }, arrayIndex) => (
                <DigitalProductJourneyStageCard
                  key={stage.uid}
                  stage={stage}
                  stageIndex={index + 1}
                  stageIsUnique={journey._stages.length === 1}
                  journey={journey}
                  isOpen={!closedPhaseUids.has(stage.uid)}
                  onToggle={() => togglePhase(stage.uid)}
                  showConnector={arrayIndex < stages.length - 1}
                  isFirstStage={arrayIndex === 0}
                  previousStage={
                    arrayIndex > 0 ? stages[arrayIndex - 1].stage : undefined
                  }
                  previousStageIndex={
                    arrayIndex > 0 ? stages[arrayIndex - 1].index + 1 : undefined
                  }
                />
              ))}
            </div>
          </Blocker>
        </Screen.Content>
      </Screen>

      <button
        type="button"
        aria-label="Ir para nível atual"
        className={`fixed z-[3] flex h-10 w-10 items-center justify-center rounded-full border-2 border-brand bg-white ${
          isVisible ? 'hidden' : ''
        }`}
        style={{ bottom: 80, right: 15 }}
        onClick={() =>
          document
            .getElementById(`level-${journey._currentLevel?.uid}`)
            ?.scrollIntoView({ behavior: 'smooth', block: 'center' })
        }
      >
        {offscreenDirection === 'up' ? <ArrowUp01Icon /> : <ArrowDown01Icon />}
      </button>
    </>
  )
}
